import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { 
  LayoutDashboard, Users, MessageSquare, PhoneCall, 
  UserCheck, X, Package, HardHat, LogOut, // LogOut icon imported
  FileText
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { role, logout } = useAdminAuth();
  
  // --- VISIBILITY ROLES ---
  const isSuperAdmin = role === "SUPER_ADMIN";
  const isAdmin = role === "ADMIN";
  const isManager = role === "MANAGER";
  const isAgent = role === "AGENT";
  const isEngineer = role === "EXECUTIVE"; // Site Engineer

  // Combined checks
  const canSeeSales = isSuperAdmin || isAdmin || isManager || role === "TEAM_LEADER" || isAgent;
  const canSeeMaterials = isSuperAdmin || isAdmin || isManager || isEngineer;
  const canApprove = isSuperAdmin || isAdmin;

  // Link Style Helper
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all rounded-lg ${
      isActive
        ? "bg-amber-600 text-white shadow-lg shadow-amber-900/20"
        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
    }`;

  return (
    <div 
      className={`
        fixed top-0 left-0 z-50 h-screen w-64 bg-[#0c0c0c] border-r border-zinc-800 flex flex-col font-[Serif] shadow-2xl
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
      `}
    >
      {/* HEADER */}
      <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
        <h1 className="text-xl font-bold text-white tracking-widest">
          LUXURIYA <span className="text-amber-500">ADMIN</span>
        </h1>
        <button onClick={onClose} className="md:hidden text-zinc-500 hover:text-white"><X size={20} /></button>
      </div>

      {/* MENU LINKS */}
      <div className="flex-1 overflow-y-auto py-6 px-3 space-y-2 scrollbar-hide">
        
        {/* 1. DASHBOARD */}
        <NavLink to="/homes-back/dashboard" className={linkClass} onClick={onClose}>
          <LayoutDashboard size={18} /> Dashboard
        </NavLink>

        {/* 2. SALES LEADS */}
        {canSeeSales && !isEngineer && (
             <NavLink to="/homes-back/lead/lead" className={linkClass} onClick={onClose}>
                <Users size={18} /> Lead Manager
             </NavLink>
        )}

        {/* 3. MATERIAL REQUISITION */}
        {canSeeMaterials && (
             <NavLink to="/homes-back/materials" className={linkClass} onClick={onClose}>
                <Package size={18} /> Material Requests
             </NavLink>
        )}

        {/* 4. TEAM MANAGEMENT */}
        {canApprove && (
          <div className="mt-4">
            <p className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-bold">Team & HR</p>
            <NavLink to="/homes-back/team/approvals" className={linkClass} onClick={onClose}>
                <UserCheck size={18} /> Approvals
            </NavLink>
            <NavLink to="/homes-back/team/subordinates" className={linkClass} onClick={onClose}>
                <Users size={18} /> My Team Hierarchy
            </NavLink>
          </div>
        )}

        {/* 5. USER QUERIES */}
        {canApprove && (
            <div className="mt-4">
                <p className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-bold">Enquiries</p>
                <NavLink to="/homes-back/consulting-queries" className={linkClass} onClick={onClose}>
                  <MessageSquare size={18} /> Consulting
                </NavLink>
                <NavLink to="/homes-back/contact-enquiries" className={linkClass} onClick={onClose}>
                  <PhoneCall size={18} /> Contact
                </NavLink>
            </div>
        )}
        {canApprove && (
            <div className="mt-4">
                <p className="px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-bold">Quatation</p>
                <NavLink to="/homes-back/quotations/create" className={linkClass} onClick={onClose}>
                  <MessageSquare size={18} /> Generate
                </NavLink>
                <NavLink to="/homes-back/quotations" className={linkClass} onClick={onClose}>
                  <FileText size={18} /> Quotations List
                </NavLink>
            </div>
        )}

      </div>

      {/* FOOTER & LOGOUT */}
      <div className="p-4 border-t border-zinc-800">
          <div className="mb-4 px-4">
             <p className="text-xs text-zinc-500">Logged in as:</p>
             <p className="text-white font-bold uppercase text-sm tracking-wider">{role?.replace("_", " ")}</p>
          </div>
          
          <button 
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-900/10 hover:text-red-300 rounded-lg transition-all text-sm font-bold uppercase tracking-wide"
          >
            <LogOut size={18} /> Logout
          </button>
      </div>
    </div>
  );
};

export default AdminSidebar;